import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { canUserCreateTicket, fetchCanRespondToTickets, fetchTickets } from "../api";
import type { Ticket } from "../types";

import cardStyles from "../styles/Card.module.css";
import tableStyles from "../styles/Table.module.css";
import buttonStyles from "../styles/Button.module.css";
import toggleStyles from "../styles/Toggle.module.css";
import dashStyles from "../styles/DashboardView.module.css";
import { GlobalContext } from "../App";
import { useAuth } from "react-oidc-context";

export default function DashboardView() {
  const auth = useAuth();
  const { site } = useParams<{site:string}>();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [pageSize, setPageSize] = useState<number | "all">(20);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const {globalSite, setSite, ticketFilter, setTicketFilter} = useContext(GlobalContext);

  const [canCreateTickets, setCanCreateTickets] = useState<boolean>(false);
  const [canRespond, setCanRespond] = useState<Boolean>(false);

  useEffect(() => {
    if (globalSite === undefined || globalSite === "") {
      if (typeof auth.user?.profile.site === "string") {
        setSite(auth.user?.profile.site)
      } else if (typeof site === "string") {
        setSite(site)
      }
    }
  }, [globalSite, site, auth.user?.profile.site, setSite])

  useEffect(() => {
    if (globalSite !== undefined && globalSite !== "") {
      fetchTickets(globalSite)
      .then((data) => {
        if (data.status === 400) {
          return;
        }
        setTickets(data);
      })
      .catch((err) => console.error("Error fetching tickets:", err));
    }
  }, [globalSite]);

  useEffect(() => {
    if (globalSite !== "") {
      if (auth.user?.profile.role === "super"
        && typeof auth.user?.profile.preferred_username === "string") {
        fetchCanRespondToTickets(globalSite, auth.user?.profile.preferred_username).then((permission) => {
          if (typeof(permission) === "boolean") {
            setCanRespond(permission)
          }
        })
      }
      else if (auth.user?.profile.sentence !== undefined
        && typeof auth.user.profile.sentence === "string") {
        fetchCanRespondToTickets(globalSite, auth.user.profile.sentence).then((permission) => {
          if (typeof(permission) === "boolean") {
            setCanRespond(permission)
          }
        })
        canUserCreateTicket(globalSite, auth.user.profile.sentence).then((permission) => {
          if (typeof(permission) === "boolean") {
            setCanCreateTickets(permission)
          }
        })
      }
    }
  }, [globalSite, auth.user?.profile.sentence, setCanRespond, setCanCreateTickets])

  useEffect(() => {
    setCurrentPage(1);
  }, [ticketFilter, startDate, endDate, pageSize]);

  function displayStatus(t:Ticket) {
    if (t.status === "Closed") {
      return (<span className={buttonStyles.statusClosed}>Closed</span>)
    }
    if (t.status === "InProgress") {
      return <span className={buttonStyles.statusInProgress}>In Progress</span>
    }
    if (t.status === "EscalatedToUnify") {
      return <span className={buttonStyles.statusEscalated}>Escalated</span>
    }
    return <span className={buttonStyles.statusOpen}>Open</span>
  }

  function newTicketButton() {
    return canCreateTickets && (
        <button
          onClick={() => navigate("/new-ticket")}
          className={`${buttonStyles.button} ${buttonStyles.primary}`}
        >
          New Ticket
        </button>
    )
  }

  const byStatus = tickets.filter((t) => {
    return t.status === ticketFilter
  });

  const byDate: Ticket[] = byStatus.filter((t) => {
    const requestedDate = new Date(t.requested);
    if (startDate) {
      const from = new Date(startDate);
      if (requestedDate < from) return false;
    }
    if (endDate) {
      const to = new Date(endDate);
      if (requestedDate > to) return false;
    }
    return true;
  });

  const totalFilteredCount = byDate.length;

  const paginatedTickets: Ticket[] = (() => {
    if (pageSize === "all") return byDate;
    const startIdx = (currentPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    return byDate.slice(startIdx, endIdx);
  })();

  const totalPages =
    pageSize === "all"
      ? 1
      : Math.max(1, Math.ceil(totalFilteredCount / (pageSize as number)));

  const onPageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === "all") {
      setPageSize("all");
    } else {
      setPageSize(parseInt(val, 10));
    }
    setCurrentPage(1);
  };

  function displayRatings() {
    let positive = tickets.filter(t => t.rating === "Positive").length
    let negative = tickets.filter(t => t.rating === "Negative").length

    let positivePercentage = (positive / (positive + negative)) * 100
    
    return (
      <div 
        style={{
          padding: "16px 16px",
          textAlign: "right"
        }}
      >User Rating: 
        <span> {positivePercentage}% positive</span>
      </div>
    )
  }

  async function navigateToTicketView(id:number) {
    if (canRespond) {
      navigate(`/dashboard/${globalSite}/support/${id}`)
    } else {
      navigate(`/dashboard/${globalSite}/${id}`)
    }
  }

  return (
    <div className={cardStyles.card}>
      <h2>Tickets</h2>
      <div style={{textAlign: "right"}}>
        {newTicketButton()}
        {displayRatings()}
      </div>
      <div className={dashStyles.filterBar}>
        {/* Date Pickers */}
        <div className={dashStyles.dateGroup}>
          <label>
            From:{" "}
            <input
              type="date"
              className={dashStyles.dateInput}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>

          <label>
            To:{" "}
            <input
              type="date"
              className={dashStyles.dateInput}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>

          {/* Show how many tickets match */}
          <div className={dashStyles.countText}>
            Showing <strong>{totalFilteredCount}</strong> ticket
            {totalFilteredCount === 1 ? "" : "s"}
          </div>
        </div>

        {/* Per‐page Selector */}
        <div className={dashStyles.pageSizeGroup}>
          <label htmlFor="pageSizeSelect">Per page:</label>
          <select
            id="pageSizeSelect"
            className={dashStyles.pageSizeSelect}
            value={pageSize === "all" ? "all" : pageSize}
            onChange={onPageSizeChange}
          >
            <option value="20">20</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      <div
        className={toggleStyles.toggleGroup}
        style={{ marginBottom: "16px" }}
      >
        <button
          className={`${toggleStyles.toggleButton} ${
            ticketFilter === "Requested" ? toggleStyles.active : ""
          }`}
          onClick={() => setTicketFilter("Requested")}
        >
          Open
        </button>
        <button
          className={`${toggleStyles.toggleButton} ${
            ticketFilter === "InProgress" ? toggleStyles.active : ""
          }`}
          onClick={() => setTicketFilter("InProgress")}
        >
          In Progress
        </button>
        <button
          className={`${toggleStyles.toggleButton} ${
            ticketFilter === "EscalatedToUnify" ? toggleStyles.active : ""
          }`}
          onClick={() => setTicketFilter("EscalatedToUnify")}
        >
          Escalated
        </button>
        <button
          className={`${toggleStyles.toggleButton} ${
            ticketFilter === "Closed" ? toggleStyles.active : ""
          }`}
          onClick={() => setTicketFilter("Closed")}
        >
          Closed
        </button>
      </div>

      <table className={tableStyles.table}>
        <thead>
          <tr>
            <th>Subject</th>
            <th>Ticket No</th>
            <th>User</th>
            <th>Cell Number</th>
            <th>Requested</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {paginatedTickets.map((t) => (
            <tr
              key={t.id}
              onClick={() => navigateToTicketView(t.id)}
              style={{ cursor: "pointer" }}
            >
              <td>{t.faults[0]}</td>
              <td>#{t.id}</td>
              <td>{t.userName}</td>
              <td>{t.location}</td>
              <td>{new Date(t.requested).toLocaleDateString()}</td>
              <td>
                {displayStatus(t)}
              </td>
            </tr>
          ))}

          {paginatedTickets.length === 0 && (
            <tr>
              <td colSpan={5} style={{ textAlign: "center", padding: "16px" }}>
                No tickets found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {pageSize !== "all" && totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            marginTop: "16px",
          }}
        >
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{
              padding: "6px 12px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              border: "none",
              borderRadius: "12px",
            }}
          >
            Prev
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            style={{
              padding: "6px 12px",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              border: "none",
              borderRadius: "12px",
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
